import { createSelector } from '@reduxjs/toolkit';

import { COVER_TYPE_TO_COVER } from '~/constants/domain-specific';
import { refDataApi } from '~/services/refData';
import type { RootState } from '~/store';
import { CoverType } from '~/types/cover';
import { UnderwritingStatus } from '~/types/refData';
import { utcDateFormatter } from '~/utils/dateFormatter';
import { formatContentKey } from '~/utils/sitecore';

import { selectBusinessDetails, selectIsPropertyOwner } from './selectBusinessDetails';

const selectCoverInclusions = (state: RootState) => state.coverInclusions.coverInclusions;

const DEFAULT_ORDER = [
  'LIABILITY',
  'PROPERTY',
  'BUSINESS_INTERRUPTION',
  'GENERAL_PROPERTY',
  'THEFT',
  'MONEY',
  'GLASS',
];

const coverTypeToCover = (cover: string) => COVER_TYPE_TO_COVER[cover as CoverType];

const DEFAULT_OPTIONS = DEFAULT_ORDER.map((c, index) => ({
  cover: c,
  priority: index + 1,
  id: coverTypeToCover(c),
}));

export const selectCoverStart = (state: RootState) => state.businessDetails?.coverStart;

/**
 * Selector for liability cover only if property owner
 *
 * Business rule to only select liability cover if the business is a property owner and the
 * tenant occupation is declined.
 */
export const selectIsLiabilityOnlyForPropertyOwner = createSelector(
  [selectIsPropertyOwner, selectBusinessDetails],
  (isPropertyOwner, businessDetails) =>
    isPropertyOwner &&
    businessDetails?.occupation?.underwritingStatus === UnderwritingStatus.Decline &&
    businessDetails?.propertyOwnerOccupation?.uw_status !== UnderwritingStatus.Decline
);

const selectDynamicCoversData = createSelector(
  [(state) => state, selectBusinessDetails, selectCoverStart],
  (state, businessDetails, coverStart) =>
    businessDetails.industry &&
    coverStart &&
    refDataApi.endpoints.getDynamicCovers.select({
      industryCode: businessDetails.industry.industryCode,
      effectiveDate: utcDateFormatter(coverStart),
    })(state).data
);

const selectDynamicCovers = createSelector(
  [selectDynamicCoversData, selectBusinessDetails, selectCoverStart],
  (dynamicCoversData, businessDetails, coverStart) => {
    if (
      !businessDetails?.industry ||
      !coverStart ||
      !dynamicCoversData ||
      !dynamicCoversData.covers
    ) {
      return DEFAULT_OPTIONS;
    }

    return dynamicCoversData.covers
      .filter((c) => coverTypeToCover(c.cover))
      .sort((a, b) => a.priority - b.priority)
      .map((c) => ({
        ...c,
        id: coverTypeToCover(c.cover),
      }));
  }
);

export const selectCoverOptions = createSelector(
  [selectDynamicCovers, selectIsLiabilityOnlyForPropertyOwner, selectCoverInclusions],
  (options, isLiabilityOnlyForPropertyOwner, ciCovers) => {
    return options
      .filter((o) => !isLiabilityOnlyForPropertyOwner || o.cover === 'LIABILITY')
      .map((c) => ({
        ...c,
        uiDescription: ciCovers
          ? ciCovers[formatContentKey(coverTypeToCover(c.cover))]?.description
          : '',
      }));
  }
);

const selectSelectCover = (state: RootState) => state.selectCover;

export const selectOrderedSelectedCovers = createSelector(
  selectSelectCover,
  selectCoverOptions,
  (selectCover, coverOptions) =>
    coverOptions.map((c) => c.id).filter((c) => selectCover.covers.includes(c))
);

export const selectOrderedCompletedCovers = createSelector(
  selectSelectCover,
  selectCoverOptions,
  (selectCover, coverOptions) =>
    coverOptions.map((c) => c.id).filter((c) => selectCover.completedCovers.includes(c))
);

export const selectOrderedValidatedCovers = createSelector(
  selectSelectCover,
  selectCoverOptions,
  (selectCover, coverOptions) =>
    coverOptions.map((c) => c.id).filter((c) => selectCover.validatedCovers.includes(c))
);

export const selectOrderedDeclinedCovers = createSelector(
  selectSelectCover,
  selectCoverOptions,
  (selectCover, coverOptions) =>
    coverOptions.map((c) => c.id).filter((c) => selectCover.declinedCovers.includes(c))
);

export const selectOrderedSelectCover = createSelector(
  selectOrderedSelectedCovers,
  selectOrderedCompletedCovers,
  selectOrderedValidatedCovers,
  selectOrderedDeclinedCovers,
  (covers, completedCovers, validatedCovers, declinedCovers) => ({
    covers,
    completedCovers,
    validatedCovers,
    declinedCovers,
  })
);
